import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MetricsResponse {
  total_events: number;
  total_cost_usd: number;
  daily_series: { date: string; events: number; cost: number }[];
}

interface PrimitiveRow {
  primitive: string;
  event_count: number;
  total_cost_usd: number;
  percentage: number;
}

interface PrimitivesResponse {
  primitives: PrimitiveRow[];
}

interface UsageSummaryResponse {
  tier: string;
  tokens_used: number;
  tokens_allocation: number;
  messages_today: number;
  messages_limit: number;
  conversations_count: number;
  cost_estimate_cents: number;
  model_breakdown: Record<string, { tokens: number; calls: number; cost_cents: number }>;
  monthly_history: Array<{ period: string; tokens: number; conversations: number; cost_cents: number }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS = [7, 30, 90] as const;
type Period = (typeof PERIOD_OPTIONS)[number];

const MODEL_COLORS: Record<string, string> = {
  'gpt-4.1': '#10b981',
  'gpt-4.1-mini': '#3b82f6',
  'gpt-4o': '#8b5cf6',
  'gpt-4o-mini': '#6366f1',
  'claude-sonnet-4-6': '#f59e0b',
  'claude-sonnet-4-5': '#f97316',
  'claude-haiku-3.5': '#ef4444',
};

function getModelColor(model: string, index: number): string {
  if (MODEL_COLORS[model]) return MODEL_COLORS[model];
  const fallback = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899'];
  return fallback[index % fallback.length]!;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatCents(cents: number): string {
  return formatCurrency(cents / 100);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function projectMonthlyCost(totalCost: number, days: number): number {
  if (days === 0) return 0;
  return (totalCost / days) * 30;
}

function getProgressColor(pct: number): string {
  if (pct < 60) return 'bg-emerald-500';
  if (pct < 80) return 'bg-yellow-500';
  if (pct < 95) return 'bg-orange-500';
  return 'bg-red-500';
}

function getProgressTextColor(pct: number): string {
  if (pct < 60) return 'text-emerald-500';
  if (pct < 80) return 'text-yellow-500';
  if (pct < 95) return 'text-orange-500';
  return 'text-red-500';
}

const TIER_LABELS: Record<string, string> = {
  jarvis_free: 'Free',
  ai_basic: 'Basic',
  ai_pro: 'Pro',
  ai_enterprise: 'Enterprise',
};

// ---------------------------------------------------------------------------
// Skeleton components
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="animate-pulse bg-muted rounded h-4 w-24 mb-3" />
      <div className="animate-pulse bg-muted rounded h-8 w-32" />
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="animate-pulse bg-muted rounded h-4 w-40 mb-4" />
      <div className="animate-pulse bg-muted rounded h-64 w-full" />
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="animate-pulse bg-muted rounded h-4 w-48 mb-4" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="animate-pulse bg-muted rounded h-8 w-full mb-2" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  title,
  value,
  subtitle,
  isLoading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  isLoading: boolean;
}) {
  if (isLoading) return <SkeletonCard />;

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

function ProgressCard({
  title,
  used,
  total,
  isLoading,
  formatFn = formatNumber,
}: {
  title: string;
  used: number;
  total: number;
  isLoading: boolean;
  formatFn?: (v: number) => string;
}) {
  if (isLoading) return <SkeletonCard />;

  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const barWidth = Math.min(100, Math.max(1, pct));

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <p className="text-sm text-muted-foreground">{title}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className="text-2xl font-bold text-foreground">{formatFn(used)}</p>
        <p className="text-sm text-muted-foreground">/ {formatFn(total)}</p>
      </div>
      <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getProgressColor(pct)}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <p className={`text-xs mt-1 font-medium ${getProgressTextColor(pct)}`}>{pct}% used</p>
    </div>
  );
}

function PeriodSelector({
  selected,
  onChange,
}: {
  selected: Period;
  onChange: (p: Period) => void;
}) {
  return (
    <div className="flex gap-1">
      {PERIOD_OPTIONS.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
            selected === p
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          {p}d
        </button>
      ))}
    </div>
  );
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}

function ChartTooltipContent({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const costValue = payload[0]?.value ?? 0;

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-sm text-muted-foreground">
        Cost: {formatCurrency(costValue)}
      </p>
    </div>
  );
}

interface ModelBarTooltipProps {
  active?: boolean;
  payload?: { payload: { model: string; tokens: number; calls: number; cost_cents: number } }[];
}

function ModelBarTooltipContent({ active, payload }: ModelBarTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const d = payload[0]?.payload;
  if (!d) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-medium text-foreground">{d.model}</p>
      <p className="text-xs text-muted-foreground">{formatCompact(d.tokens)} tokens</p>
      <p className="text-xs text-muted-foreground">{formatNumber(d.calls)} calls</p>
      <p className="text-xs text-muted-foreground">{formatCents(d.cost_cents)}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Usage() {
  const [days, setDays] = useState<Period>(30);

  const metricsQuery = useQuery({
    queryKey: ['analytics-metrics', days],
    queryFn: () => api<MetricsResponse>(`analytics/metrics?days=${days}`),
  });

  const primitivesQuery = useQuery({
    queryKey: ['analytics-primitives', days],
    queryFn: () => api<PrimitivesResponse>(`analytics/primitives?days=${days}`),
  });

  const summaryQuery = useQuery({
    queryKey: ['ai-usage'],
    queryFn: () => api<UsageSummaryResponse>('analytics/usage-summary'),
    staleTime: 60_000,
  });

  const metrics = metricsQuery.data;
  const primitives = primitivesQuery.data?.primitives ?? [];
  const sortedPrimitives = [...primitives].sort(
    (a, b) => b.total_cost_usd - a.total_cost_usd,
  );
  const activePrimitiveCount = primitives.length;
  const projectedCost = metrics
    ? projectMonthlyCost(metrics.total_cost_usd, days)
    : 0;

  const chartData = (metrics?.daily_series ?? []).map((d) => ({
    ...d,
    label: formatDate(d.date),
  }));

  const summary = summaryQuery.data;
  const isSummaryLoading = summaryQuery.isLoading;
  const isMetricsLoading = metricsQuery.isLoading;
  const isPrimitivesLoading = primitivesQuery.isLoading;

  // Model breakdown chart data
  const modelBreakdownData = summary?.model_breakdown
    ? Object.entries(summary.model_breakdown)
        .map(([model, data]) => ({ model, ...data }))
        .sort((a, b) => b.tokens - a.tokens)
    : [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">
          Usage Dashboard
        </h1>
        {summary && (
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
            {TIER_LABELS[summary.tier] ?? summary.tier}
          </span>
        )}
      </div>
      <p className="text-muted-foreground mb-8">
        Monitor AI token usage, costs, and model breakdown.
      </p>

      {/* Allocation cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <ProgressCard
          title="Tokens This Month"
          used={summary?.tokens_used ?? 0}
          total={summary?.tokens_allocation ?? 0}
          isLoading={isSummaryLoading}
          formatFn={formatCompact}
        />
        <ProgressCard
          title="Messages Today"
          used={summary?.messages_today ?? 0}
          total={summary?.messages_limit ?? 0}
          isLoading={isSummaryLoading}
        />
        <StatCard
          title="Conversations"
          value={summary ? formatNumber(summary.conversations_count) : '--'}
          subtitle="This billing period"
          isLoading={isSummaryLoading}
        />
        <StatCard
          title="Cost Estimate"
          value={summary ? formatCents(summary.cost_estimate_cents) : '--'}
          subtitle="Current period"
          isLoading={isSummaryLoading}
        />
      </div>

      {/* Model usage breakdown */}
      {isSummaryLoading ? (
        <SkeletonChart />
      ) : modelBreakdownData.length > 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-4">
            Model Usage Breakdown
          </h2>
          <ResponsiveContainer width="100%" height={Math.max(200, modelBreakdownData.length * 48)}>
            <BarChart
              data={modelBreakdownData}
              layout="vertical"
              margin={{ top: 4, right: 40, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                horizontal={false}
              />
              <XAxis
                type="number"
                tickFormatter={(v: number) => formatCompact(v)}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="model"
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                width={140}
              />
              <Tooltip content={<ModelBarTooltipContent />} />
              <Bar dataKey="tokens" radius={[0, 4, 4, 0]}>
                {modelBreakdownData.map((entry, index) => (
                  <Cell key={entry.model} fill={getModelColor(entry.model, index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* API cost chart */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total API Calls"
          value={metrics ? formatNumber(metrics.total_events) : '--'}
          isLoading={isMetricsLoading}
        />
        <StatCard
          title="Total Cost"
          value={metrics ? formatCurrency(metrics.total_cost_usd) : '--'}
          isLoading={isMetricsLoading}
        />
        <StatCard
          title="Active Primitives"
          value={
            primitivesQuery.data ? String(activePrimitiveCount) : '--'
          }
          isLoading={isPrimitivesLoading}
        />
        <StatCard
          title="Projected Monthly Cost"
          value={metrics ? formatCurrency(projectedCost) : '--'}
          isLoading={isMetricsLoading}
        />
      </div>

      {/* Chart section */}
      {isMetricsLoading ? (
        <SkeletonChart />
      ) : (
        <div className="rounded-lg border border-border bg-card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-muted-foreground">
              Daily Cost
            </h2>
            <PeriodSelector selected={days} onChange={setDays} />
          </div>

          {chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              No usage data for this period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart
                data={chartData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="cost"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#costGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Usage trend - last 6 months */}
      {isSummaryLoading ? (
        <SkeletonTable />
      ) : summary?.monthly_history && summary.monthly_history.length > 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-4">
            Usage Trend (Last 6 Months)
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-muted-foreground font-medium py-3 pr-4">Period</th>
                  <th className="text-right text-muted-foreground font-medium py-3 px-4">Tokens</th>
                  <th className="text-right text-muted-foreground font-medium py-3 px-4">Conversations</th>
                  <th className="text-right text-muted-foreground font-medium py-3 pl-4">Cost</th>
                </tr>
              </thead>
              <tbody>
                {summary.monthly_history.map((row) => (
                  <tr key={row.period} className="border-b border-border last:border-0">
                    <td className="py-3 pr-4 text-foreground font-medium">{formatPeriod(row.period)}</td>
                    <td className="py-3 px-4 text-right text-foreground tabular-nums">{formatCompact(row.tokens)}</td>
                    <td className="py-3 px-4 text-right text-foreground tabular-nums">{formatNumber(row.conversations)}</td>
                    <td className="py-3 pl-4 text-right text-foreground tabular-nums">{formatCents(row.cost_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Primitives breakdown table */}
      {isPrimitivesLoading ? (
        <SkeletonTable />
      ) : (
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-sm font-medium text-muted-foreground mb-4">
            Primitives Breakdown
          </h2>

          {sortedPrimitives.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No primitive usage recorded for this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-muted-foreground font-medium py-3 pr-4">
                      Primitive
                    </th>
                    <th className="text-right text-muted-foreground font-medium py-3 px-4">
                      Events
                    </th>
                    <th className="text-right text-muted-foreground font-medium py-3 px-4">
                      Cost
                    </th>
                    <th className="text-right text-muted-foreground font-medium py-3 pl-4">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPrimitives.map((row) => (
                    <tr
                      key={row.primitive}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-3 pr-4 text-foreground font-medium">
                        <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                          {row.primitive}
                        </code>
                      </td>
                      <td className="py-3 px-4 text-right text-foreground tabular-nums">
                        {formatNumber(row.event_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground tabular-nums">
                        {formatCurrency(row.total_cost_usd)}
                      </td>
                      <td className="py-3 pl-4 text-right text-muted-foreground tabular-nums">
                        {row.percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
